
import {Feature, stopModalLoading} from '../reducers/environmentReducer';
import {Carts, DefaultCart, updateCartCalculation} from '../reducers/cartReducer';
import {PeachPayCustomer} from '../reducers/peachPayCustomerReducer';
import {PeachPayOrder} from '../reducers/peachPayOrderReducer';
import {store} from '../store';
import {type ICartCalculationResponse} from '../../../@type/woocommerce/cart-calculation';
import {getLocaleText} from '../util/translation';
import {PaymentConfiguration, setSelectedPaymentGateway, updateAvailableGateways, updateGatewayDetails} from '../reducers/paymentConfigurationReducer';
import {type IResponse} from '../../../@type/response';
import {stripHtml, $qsAll, $qs, isMobile} from '../../../@shared/ts/dom';
import {type WCOrder} from '../../../@type/woocommerce/order';
import {captureSentryException} from '../../../@shared/ts/sentry';
import {getCaptchaToken} from '../features/botProtection';
import {type Maybe, maybeFetchWP} from '../../../@shared/ts/maybe';
import {getErrorString} from '../../../@shared/ts/error';
import {FeatureFlag} from '../../../@type/features';
import {type PaymentData, type PaymentAttempt} from '../../../@shared/ts/paymentAttempt';
import {type TransactionUpdate} from '../../../@shared/ts/transaction';
import {DebounceAbortController} from '../util/debounce';

export interface OrderService {
	placeOrder: (transaction: Transaction, extraFormData?: Record<string, string>) => Promise<Maybe<WCOrder>>;
	startTransaction: (paymentGateway: string) => Promise<Maybe<Transaction>>;
}

export interface Transaction {
	getId: () => string;
	update: (options: TransactionUpdate) => void;
	complete: (options?: TransactionUpdate) => Promise<boolean>;
}

export function getOrderService(): OrderService {
	return {
		placeOrder,
		async startTransaction(gatewayId: string): Promise<Maybe<Transaction>> {
			const transactionId = await createPaymentTransaction(gatewayId);
			let complete = false;
			if (!transactionId) {
				return {error: new Error('Failed to create a transaction. Please refresh the page and try again.')};
			}

			const transactionUpdates: TransactionUpdate[] = [];

			return {
				result: {
					getId() {
						return transactionId;
					},
					update(options: TransactionUpdate) {
						transactionUpdates.push(options);
					},
					async complete(options?: TransactionUpdate) {
						if (complete) {
							console.error('Developer error: Transaction already completed.');
							return false;
						}

						complete = true;

						if (options) {
							transactionUpdates.push(options);
						}

						if (transactionUpdates.length === 0) {
							return false;
						}

						const update = transactionUpdates.reduce((pt, ct) => ({...pt, ...ct}), {});
						return updatePaymentTransaction(transactionId, update);
					},
				},
			};
		},
	};
}

async function placeOrder(transaction: Transaction, extraFormData: Record<string, string> = {}): Promise<Maybe<WCOrder>> {
	const checkoutURL = Feature.metadata<string>(FeatureFlag.EXPRESS_CHECKOUT, 'checkout_url');
	const checkoutNonce = Feature.dynamicMetadata<string>(FeatureFlag.EXPRESS_CHECKOUT, 'checkout_nonce');

	if (!checkoutURL || !checkoutNonce) {
		return {error: new Error('Invalid checkout URL or nonce')};
	}

	const formData = PeachPayOrder.formData();
	formData.append('woocommerce-process-checkout-nonce', checkoutNonce);

	const gatewayId = PaymentConfiguration.selectedGateway();
	if (gatewayId !== 'peachpay_free') {
		formData.append('payment_method', gatewayId);
	}

	formData.append('peachpay_transaction_id', transaction.getId());
	formData.append('peachpay_captcha_token', await getCaptchaToken());

	formData.append('terms', '1');
	formData.append('european_gdpr', '1');

	formData.append('ct-ultimate-gdpr-consent-field', 'on');
	formData.append('ct-ultimate-gdpr-consent-field-additional', '1');
	formData.append('ct-ultimate-gdpr-consent-field-additional', '1');
	formData.append('ct-ultimate-gdpr-consent-field-label-text', '1');

	formData.append('wc_order_attribution_source_type', 'typein');
	formData.append('wc_order_attribution_utm_source', '(direct)'); // This code sets the orders placed via Express Checkout as Direct (source type) orders.

	for (const [key, value] of Object.entries(extraFormData)) {
		formData.append(key, value);
	}

	const {error: orderError, result: orderResult} = await maybeFetchWP<WCOrder>(checkoutURL + '&pp-express-checkout', {
		method: 'POST',
		credentials: 'same-origin',
		body: formData,
	});

	if (orderError || !orderResult || orderResult.result !== 'success') {
		if (orderError) {
			if (orderError instanceof Error) {
				captureSentryException(orderError);
			} else {
				captureSentryException(new Error(getErrorString(orderError)));
			}

			const errorMessage = getErrorString(orderError);
			displayPaymentErrorMessage(errorMessage);

			await transaction.complete({
				note: errorMessage,
			});

			return {error: orderError, result: orderResult};
		}

		if (orderResult?.result === 'failure') {
			const errorMessage = stripHtml(orderResult.messages, null).trim() || getLocaleText('Unknown error occured while placing order. Please refresh the page and try again.');
			displayPaymentErrorMessage(errorMessage);

			await transaction.complete({
				note: errorMessage,
			});

			// In the scenario the order fails but a customer was registered all the existing nonces are
			// now invalid. We need to refresh the checkout to get new ones.
			if (orderResult.refresh || orderResult.reload) {
				await requestCartCalculation();
			}

			return {error: orderError, result: orderResult};
		}

		const errorMessage = getLocaleText('Unknown error occured while placing order. Please refresh the page and try again.');
		displayPaymentErrorMessage(errorMessage);

		await transaction.complete({
			note: errorMessage,
		});

		// Any failure after an order risks invalid nonces in the case of a user being logged in. To prevent
		// possible problems we will get a new cart calculation.
		await requestCartCalculation();
	}

	return {error: orderError, result: orderResult};
}

export const cartCalculationAbortController = new DebounceAbortController();

/**
 * Calls a endpoint in the PeachPay Plugin to recalculate all the summary cost
 */
export async function requestCartCalculation(sync: 'push' | 'pull' = 'push'): Promise<void> {
	// We want to ensure any debounced cart calculations are canceled. So we call
	// abort here. Any debounced functions which call `requestCartCalculation` should
	// pass this same abort controller.
	cartCalculationAbortController.abort();

	const cartUrl = Feature.metadata<string>(FeatureFlag.EXPRESS_CHECKOUT, 'calculation_url');
	if (!cartUrl) {
		throw new Error('Invalid cart calculation URL');
	}

	const formData = new FormData();
	if (sync === 'push') {
		formData.append('billing_email', PeachPayCustomer.billing.email());
		formData.append('billing_first_name', PeachPayCustomer.billing.firstName());
		formData.append('billing_last_name', PeachPayCustomer.billing.lastName());
		formData.append('billing_phone', PeachPayCustomer.billing.phone());
		formData.append('billing_company', PeachPayCustomer.billing.company());
		formData.append('billing_address_1', PeachPayCustomer.billing.address1());
		formData.append('billing_address_2', PeachPayCustomer.billing.address2());
		formData.append('billing_city', PeachPayCustomer.billing.city());
		formData.append('billing_state', PeachPayCustomer.billing.state());
		formData.append('billing_country', PeachPayCustomer.billing.country());
		formData.append('billing_postcode', PeachPayCustomer.billing.postal());

		if (PeachPayCustomer.shipToDifferentAddress()) {
			formData.append('ship_to_different_address', '1');
			formData.append('shipping_first_name', PeachPayCustomer.shipping.firstName());
			formData.append('shipping_last_name', PeachPayCustomer.shipping.lastName());
			formData.append('shipping_phone', PeachPayCustomer.shipping.phone());
			formData.append('shipping_company', PeachPayCustomer.shipping.company());
			formData.append('shipping_address_1', PeachPayCustomer.shipping.address1());
			formData.append('shipping_address_2', PeachPayCustomer.shipping.address2());
			formData.append('shipping_city', PeachPayCustomer.shipping.city());
			formData.append('shipping_state', PeachPayCustomer.shipping.state());
			formData.append('shipping_country', PeachPayCustomer.shipping.country());
			formData.append('shipping_postcode', PeachPayCustomer.shipping.postal());
		}

		for (const [packageKey, methodKey] of Object.entries(PeachPayOrder.collectSelectedShipping())) {
			formData.append(`shipping_method[${packageKey}]`, methodKey);
		}

		const gatewayId = PaymentConfiguration.selectedGateway();
		if (gatewayId !== 'peachpay_free') {
			formData.append('payment_method', gatewayId);
		}
	}

	const {error: calculationError, result: calculationResult} = await maybeFetchWP<ICartCalculationResponse>(cartUrl, {
		method: 'POST',
		credentials: 'same-origin',
		body: formData,
	});

	if (calculationError || !calculationResult || !calculationResult.success) {
		if (calculationError) {
			if (calculationError instanceof Error) {
				captureSentryException(calculationError);
			} else {
				captureSentryException(new Error(getErrorString(calculationError)));
			}
		} else if (calculationResult && !calculationResult.success && calculationResult.message) {
			captureSentryException(new Error(calculationResult.message), {
				notices: calculationResult.notices,
			});
		} else {
			captureSentryException(new Error('Unknown error occured while recalculating cart.'));
		}

		return;
	}

	consumeCartCalculationResponse(calculationResult, sync === 'pull');
}

export function consumeCartCalculationResponse(response: ICartCalculationResponse, pull = false) {
	if (response.notices) {
		if (response.notices.error) {
			let cartErrors = '';
			for (const errorNotice of response.notices.error) {
				renderOrderNotice(errorNotice.notice);
				cartErrors += errorNotice.notice;
			}

			setOrderError(cartErrors);
		}

		if (response.notices.success) {
			for (const successNotice of response.notices.success) {
				renderOrderNotice(successNotice.notice);
			}
		}

		if (response.notices.notice) {
			for (const notice of response.notices.notice) {
				renderOrderNotice(notice.notice);
			}
		}
	}

	if (response.data) {
		setOrderError('');

		if (pull) {
			PeachPayCustomer.billing.email(response.data.customer['billing_email']);
			PeachPayCustomer.billing.firstName(response.data.customer['billing_first_name']);
			PeachPayCustomer.billing.lastName(response.data.customer['billing_last_name']);
			PeachPayCustomer.billing.phone(response.data.customer['billing_phone']);
			PeachPayCustomer.billing.company(response.data.customer['billing_company']);
			PeachPayCustomer.billing.address1(response.data.customer['billing_address_1']);
			PeachPayCustomer.billing.address2(response.data.customer['billing_address_2']);
			PeachPayCustomer.billing.city(response.data.customer['billing_city']);
			PeachPayCustomer.billing.state(response.data.customer['billing_state']);
			PeachPayCustomer.billing.postal(response.data.customer['billing_postcode']);
			PeachPayCustomer.billing.country(response.data.customer['billing_country']);

			PeachPayCustomer.shipping.firstName(response.data.customer['shipping_first_name']);
			PeachPayCustomer.shipping.lastName(response.data.customer['shipping_last_name']);
			PeachPayCustomer.shipping.phone(response.data.customer['shipping_phone']);
			PeachPayCustomer.shipping.company(response.data.customer['shipping_company']);
			PeachPayCustomer.shipping.address1(response.data.customer['shipping_address_1']);
			PeachPayCustomer.shipping.address2(response.data.customer['shipping_address_2']);
			PeachPayCustomer.shipping.city(response.data.customer['shipping_city']);
			PeachPayCustomer.shipping.state(response.data.customer['shipping_state']);
			PeachPayCustomer.shipping.postal(response.data.customer['shipping_postcode']);
			PeachPayCustomer.shipping.country(response.data.customer['shipping_country']);

			$qs('#pp-billing-form')?.dispatchEvent(new Event('change'));
		}

		store.dispatch(updateCartCalculation(response.data.cart_calculation_record));
		store.dispatch(updateGatewayDetails(response.data.gateway_availability_details));

		if (DefaultCart.contents().length >= 1 && Carts.total() === 0) {
			store.dispatch(updateAvailableGateways(['peachpay_free']));
		} else {
			store.dispatch(updateAvailableGateways(response.data.available_gateway_ids));
		}

		if (DefaultCart.contents().length === 0) {
			setOrderError(`<span>${getLocaleText('Cart is empty')}</span>`);
		}

		const gatewayId = PaymentConfiguration.checkEligibleOrFindAlternate(PaymentConfiguration.selectedGateway());
		if (gatewayId) {
			store.dispatch(setSelectedPaymentGateway(gatewayId));
		} else {
			store.dispatch(setSelectedPaymentGateway(''));
			setOrderError(`<span>${getLocaleText('There are no payment methods available')}</span>`);
		}
	}
}

function setOrderError(errorMessage: string) {
	$qsAll('.pp-continue-order-error', element => {
		element.innerHTML = '';
		element.classList.remove('pp-error');
	});

	if (errorMessage !== '') {
		$qsAll('.pp-continue-order-error', element => {
			element.innerHTML = errorMessage;
			element.classList.add('pp-error');
		});
	}
}

export function renderOrderNotice(data: string) {
	const message = stripHtml(data);

	const $noticeElement = document.createElement('div');
	$noticeElement.classList.add('pp-notice');
	$noticeElement.innerHTML = message;

	if (isMobile()) {
		$qs('#pp-notice-container-mobile', $el => {
			$el.classList.remove('hide');
			$el.insertAdjacentElement('afterbegin', $noticeElement);

			setTimeout(() => {
				$el.classList.add('hide');
			}, 10050);
		});
	} else {
		$qs('#pp-notice-container-new', $el => {
			$el.classList.remove('hide');
			$el.insertAdjacentElement('afterbegin', $noticeElement);

			setTimeout(() => {
				$el.classList.add('hide');
			}, 10050);
		});
	}

	// Remove any 'pp-notice' elements currently in DOM after 10 seconds.
	setTimeout(() => {
		$noticeElement?.remove();
	}, 10000);
}

async function createPaymentTransaction(paymentGateway: string): Promise<string | null> {
	const createTransactionURL = Feature.metadata<string>(FeatureFlag.EXPRESS_CHECKOUT, 'create_transaction_url');
	if (!createTransactionURL) {
		captureSentryException(new Error('Invalid or missing create transaction URL'));
		return null;
	}

	const formData = new FormData();
	formData.append('gateway_id', paymentGateway);
	formData.append('checkout_location', 'checkout-window');

	try {
		const response = await fetch(createTransactionURL, {
			method: 'POST',
			body: formData,
		});

		const body = await response.json() as IResponse<{transaction_id: string}>;

		if (!response.ok || !body.success) {
			captureSentryException(new Error('Failed to create a new payment transaction'));
			return null;
		}

		return body.data.transaction_id;
	} catch (error) {
		if (error instanceof Error) {
			captureSentryException(new Error(`Unknown error while attempting to create a new payment transaction :: ${error.toString()}`));
		}

		return null;
	}
}

async function updatePaymentTransaction(transactionId: string, options: TransactionUpdate): Promise<boolean> {
	const updateTransactionURL = Feature.metadata<string>(FeatureFlag.EXPRESS_CHECKOUT, 'update_transaction_url');
	if (!updateTransactionURL) {
		captureSentryException(new Error('Invalid or missing update transaction URL'), {
			transaction_id: transactionId,
		});

		return false;
	}

	try {
		const formData = new FormData();
		formData.append('transaction_id', transactionId);

		if (options.paymentStatus) {
			formData.append('payment_status', options.paymentStatus);
		}

		if (options.orderStatus) {
			formData.append('order_status', options.orderStatus);
		}

		if (options.note) {
			formData.append('note', options.note);
		}

		const response = await fetch(updateTransactionURL, {
			method: 'POST',
			body: formData,
		});

		const responseBody = await response.json() as IResponse<{transaction_id: string}>;

		if (!response.ok || !responseBody.success) {
			captureSentryException(new Error('Failed to update an existing payment transaction'), {
				transaction_id: transactionId,
			});

			return false;
		}

		return true;
	} catch (error) {
		if (error instanceof Error) {
			captureSentryException(new Error(`Unknown error while attempting to update a existing payment transaction :: ${error.toString()}`), {
				transaction_id: transactionId,
			});
		}

		return false;
	}
}

export function displayPaymentErrorMessage(error?: string): void {
	$qsAll('.pp-pm-error-text', $el => {
		$el.remove();
	});

	if (!error) {
		return;
	}

	const gatewayId = PaymentConfiguration.selectedGateway();
	const $container = $qsAll(`div.pp-pm-saved-option[data-gateway="${gatewayId}"]`);

	$container.forEach($el => {
		$el.insertAdjacentHTML('beforebegin', /* html */ `<div class="pp-pm-error-text"><span>${error}</span></div>`);
	});
}

/**
 * Creates a payment attempt for the checkout page.
 */
export function createExpressCheckoutPaymentAttempt(orderService: OrderService): PaymentAttempt {
	let transaction: Transaction | null = null;
	let paymentResult: PaymentData;

	return {
		getTransactionId() {
			if (transaction) {
				return transaction.getId();
			}

			if (transaction === null) {
				throw new Error('Transaction failed to be created.');
			} else {
				throw new Error('Transaction not created yet.');
			}
		},
		getOrderId() {
			if (paymentResult?.order_id) {
				return paymentResult.order_id;
			}

			throw new Error('Order not created yet.');
		},
		stopLoading() {
			store.dispatch(stopModalLoading());
		},
		setPaymentMessage: displayPaymentErrorMessage,

		async submitOrder<T extends PaymentData>(_dataType: string, extraFields: Record<string, string> = {}): Promise<T> {
			const {error, result} = await orderService.placeOrder(transaction!, {
				peachpay_transaction_id: transaction!.getId(),
				...extraFields,
			});

			if (error || !result || result.result !== 'success') {
				throw new Error(error ? getErrorString(error) : getLocaleText('Unknown error occured while placing order. Please refresh the page and try again.'));
			}

			const dataURL = new URL(result.redirect);
			const [name, data] = dataURL.hash.split('=');
			if (name !== '#payment_data' || !data) {
				throw new Error('Failed to retrieve paypal payment details from url: ' + result.redirect);
			}

			paymentResult = JSON.parse(atob(decodeURIComponent(data))) as PaymentData;

			return paymentResult as T;
		},
		redirectCancel() {
			if (!paymentResult) {
				throw new Error('Payment result not set yet.');
			}

			window.top!.location.href = paymentResult.cancel_url;
		},
		redirectSuccess() {
			if (!paymentResult) {
				throw new Error('Payment result not set yet.');
			}

			window.top!.location.href = paymentResult.success_url;
		},

		async createTransaction(gatewayId: string) {
			const {error, result} = await orderService.startTransaction(gatewayId);
			if (result) {
				transaction = result;
				return;
			}

			throw error;
		},
		async completeTransaction(update: TransactionUpdate) {
			await transaction!.complete(update);
		},

		featureEnabled(feature: FeatureFlag) {
			return Feature.enabled(feature);
		},
		featureMetadata<T = string>(feature: FeatureFlag, key: string) {
			const metadata = Feature.metadata<T>(feature, key);
			if (metadata === null) {
				throw new Error(`Feature metadata for '${feature}' with metadata key '${key}' does not exist`);
			}

			return metadata;
		},
	} satisfies PaymentAttempt;
}
